import React, { useState } from 'react';
import { Box, IconButton, Paper, SxProps, Theme } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import TokenDisplay from './TokenDisplay';
import TokenSelector from './TokenSelector';
import { TokenInfo } from '../store/dexConfigSlice';
import { useDexConfigRedux } from '../hooks/useDexConfigRedux';

/**
 * TokenPair组件属性接口
 */
interface TokenPairProps {
  /**
   * 代币1信息
   */
  token1: TokenInfo | null;
  /**
   * 代币2信息
   */
  token2: TokenInfo | null;
  /**
   * 代币1变更回调
   */
  onToken1Change: (token: TokenInfo) => void;
  /**
   * 代币2变更回调
   */
  onToken2Change: (token: TokenInfo) => void;
  /**
   * 交换代币回调
   */
  onSwapTokens?: () => void;
  /**
   * 自定义样式
   */
  sx?: SxProps<Theme>;
  /**
   * 是否禁用
   */
  disabled?: boolean;
}

/**
 * 代币对组件
 * 用于同时选择和显示交易对中的两个代币
 */
const TokenPair: React.FC<TokenPairProps> = ({
  token1,
  token2,
  onToken1Change,
  onToken2Change,
  onSwapTokens,
  sx,
  disabled = false
}) => {
  const { tokens } = useDexConfigRedux();
  const [token1SelectorOpen, setToken1SelectorOpen] = useState<boolean>(false);
  const [token2SelectorOpen, setToken2SelectorOpen] = useState<boolean>(false);

  // 打开代币1选择器
  const handleOpenToken1Selector = () => {
    if (disabled) return;
    setToken1SelectorOpen(true);
  };

  // 打开代币2选择器
  const handleOpenToken2Selector = () => {
    if (disabled) return;
    setToken2SelectorOpen(true);
  };

  // 关闭代币1选择器
  const handleCloseToken1Selector = () => {
    setToken1SelectorOpen(false);
  };

  // 关闭代币2选择器
  const handleCloseToken2Selector = () => {
    setToken2SelectorOpen(false);
  };

  // 处理代币1选择
  const handleToken1Select = (token: TokenInfo) => {
    onToken1Change(token);
  };

  // 处理代币2选择
  const handleToken2Select = (token: TokenInfo) => {
    onToken2Change(token);
  };

  // 处理代币交换
  const handleSwapTokens = () => {
    if (disabled || !onSwapTokens) return;
    onSwapTokens();
  };

  // 获取排除地址列表
  const getExcludeAddresses = (isToken1: boolean): string[] => {
    const excludeList: string[] = [];
    
    // 如果已选择了另一个代币，将其地址添加到排除列表
    if (isToken1 && token2?.address) {
      excludeList.push(token2.address.toLowerCase());
    } else if (!isToken1 && token1?.address) {
      excludeList.push(token1.address.toLowerCase());
    }
    
    return excludeList;
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Paper
        elevation={0}
        sx={{
          m: "20px 0",
          borderRadius: 2,
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
          {/* 代币1选择 */}
          <Box sx={{ flex: 1 }}>
            <TokenDisplay
              token={token1}
              onClick={handleOpenToken1Selector}
              label="从"
              disabled={disabled}
            />
          </Box>
          
          {/* 交换按钮 */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mx: 0.5 }}>
            <IconButton
              size="small"
              onClick={handleSwapTokens}
              disabled={disabled || !token1 || !token2}
              sx={{
                width: 28,
                height: 28,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover'
                },
                zIndex: 1
              }}
            >
              <SwapVertIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
            </IconButton>
          </Box>
          
          {/* 代币2选择 */}
          <Box sx={{ flex: 1 }}>
            <TokenDisplay
              token={token2}
              onClick={handleOpenToken2Selector}
              label="到"
              disabled={disabled}
            />
          </Box>
        </Box>
      </Paper>

      {/* 代币1选择器 */}
      <TokenSelector
        open={token1SelectorOpen}
        onClose={handleCloseToken1Selector}
        onSelectToken={handleToken1Select}
        title="选择代币"
        currentToken={token1}
        excludeAddresses={getExcludeAddresses(true)}
      />

      {/* 代币2选择器 */}
      <TokenSelector
        open={token2SelectorOpen}
        onClose={handleCloseToken2Selector}
        onSelectToken={handleToken2Select}
        title="选择代币"
        currentToken={token2}
        excludeAddresses={getExcludeAddresses(false)}
      />
    </Box>
  );
};

export default TokenPair;
